import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { extractErrorMessage } from '../../api/errors';
import { Track, TrackDetailsInput, useUpdateTrackDetails } from '../../queries/tracks';

interface Props {
  track: Track;
  onClose: () => void;
}

const inputClass = 'mt-1 w-full rounded-md border border-input bg-background px-2 py-2 text-sm';
const textareaClass = `${inputClass} min-h-[70px]`;

const SUMMARY_FIELDS: { key: keyof TrackDetailsInput; label: string }[] = [
  { key: 'track_notes', label: 'Track notes (never overwritten by the summary refresh)' },
  { key: 'track_summary', label: 'General summary' },
  { key: 'track_summary_firm', label: 'Firm' },
  { key: 'track_summary_good', label: 'Good' },
  { key: 'track_summary_soft', label: 'Soft' },
  { key: 'track_summary_heavy', label: 'Heavy' },
];

const REQUIRED: (keyof TrackDetailsInput)[] = ['name', 'code', 'track_id', 'state', 'type'];

export function EditTrackDetailsDialog({ track, onClose }: Props) {
  const update = useUpdateTrackDetails();
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<TrackDetailsInput>({
    track_id: track.track_id,
    code: track.code,
    name: track.name,
    state: track.state,
    type: track.type,
    display_name: track.display_name ?? '',
    alternative_name: track.alternative_name ?? '',
    track_conditions_name: track.track_conditions_name ?? '',
    track_summary: track.track_summary ?? '',
    track_summary_firm: track.track_summary_firm ?? '',
    track_summary_good: track.track_summary_good ?? '',
    track_summary_soft: track.track_summary_soft ?? '',
    track_summary_heavy: track.track_summary_heavy ?? '',
    track_notes: track.track_notes ?? '',
  });

  const set = (patch: Partial<TrackDetailsInput>) => setForm((f) => ({ ...f, ...patch }));

  const handleSave = () => {
    const missing = REQUIRED.find((k) => !String(form[k] ?? '').trim());
    if (missing) {
      setError('Name, code, track ID, state and type are all required.');
      return;
    }
    setError(null);
    // The URL keeps the ORIGINAL track_id so a changed track_id still targets the right row.
    update.mutate(
      { trackId: track.track_id, details: form },
      { onSuccess: onClose, onError: (err) => setError(extractErrorMessage(err, 'Failed to save track details.')) },
    );
  };

  const textField = (label: string, key: keyof TrackDetailsInput, required = false) => (
    <label className="block text-sm">
      {label}{required && <span className="text-destructive"> *</span>}
      <input className={inputClass} value={(form[key] as string) ?? ''} onChange={(e) => set({ [key]: e.target.value } as Partial<TrackDetailsInput>)} />
    </label>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg border border-border bg-background p-6" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-semibold">Edit track — {track.name}</h3>
        <p className="mt-1 text-sm text-muted-foreground">Edit the track's identity, descriptive fields, permanent track notes, and per-condition summaries. Required fields are marked *.</p>

        <div className="mt-4 grid grid-cols-2 gap-3">
          {textField('Track name', 'name', true)}
          {textField('Code', 'code', true)}
          {textField('Track ID', 'track_id', true)}
          {textField('State', 'state', true)}
          {textField('Track type', 'type', true)}
          {textField('Display name', 'display_name')}
          {textField('Alternative name', 'alternative_name')}
          <div className="col-span-2">{textField('Track-conditions name', 'track_conditions_name')}</div>

          {SUMMARY_FIELDS.map((f) => (
            <label key={f.key} className="col-span-2 block text-sm">
              {f.label}
              <textarea className={textareaClass} value={(form[f.key] as string) ?? ''} onChange={(e) => set({ [f.key]: e.target.value } as Partial<TrackDetailsInput>)} />
            </label>
          ))}

          {error && <p className="col-span-2 text-sm text-destructive">{error}</p>}
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-md border border-input px-3 py-1.5 text-sm font-medium hover:bg-accent">
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={update.isPending}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
          >
            {update.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
